/**
 * 3D models (STL) as a first-class blob — backend: internal/twin/products.go
 * (POST /v1/twin/models), served by the existing GET /v1/twin/images/{id}.
 *
 * An STL is uploaded against a product or a pattern (subject_kind + subject_id),
 * stored inline in Postgres as an image_blobs row with content_type 'model/stl',
 * and fetched back as an authenticated Blob → object URL → STLLoader (a raw fetch
 * can't carry the auth header, same as the image path).
 */

import { api } from "@/lib/api-client";
import type { ImageDatapoint } from "./images";

export type ModelSubjectKind = "product" | "pattern";

export interface UploadModelInput {
  file: Blob;
  subjectKind: ModelSubjectKind;
  subjectId: string;
  filename?: string;
}

export const models = {
  /** Upload an STL against a product/pattern (multipart). Returns the blob's id. */
  upload: (input: UploadModelInput) => {
    const fd = new FormData();
    fd.append("file", input.file, input.filename ?? "model.stl");
    fd.append("subject_kind", input.subjectKind);
    fd.append("subject_id", input.subjectId);
    return api.postForm<ImageDatapoint>("/v1/twin/models", fd);
  },

  /** Fetch one model's bytes as an authenticated Blob. */
  blob: (modelId: string) => api.getBlob(`/v1/twin/images/${modelId}`),

  /** Build an object-URL for a model; caller revokes when done. */
  objectUrl: async (modelId: string): Promise<string> => {
    const blob = await api.getBlob(`/v1/twin/images/${modelId}`);
    return URL.createObjectURL(blob);
  },
};
